// Atlas surface-coloring helpers, extracted verbatim from NeuroViewer.jsx.
// Pure functions (no React/THREE deps). Shared by live atlas coloring
// (loadGiiFile + viewer-init atlas load) and BrainVisualizer.

export const looksLikeAtlasIntensityFile = (fileName = '') => {
    return /^atlas(?:_|\.|$)/i.test(fileName);
};


export const parseTextIntensityValues = (text) => (
    new Float32Array(
        String(text || '')
            .trim()
            .split(/\s+/)
            .map(Number)
            .filter(Number.isFinite)
    )
);


export const parseBrainBrowserColorMap = (text) => {
    const colors = [];
    String(text || '').trim().split(/\n/).forEach((line) => {
        const values = line.trim().split(/\s+/).map(Number).filter(Number.isFinite);
        if (values.length >= 5) {
            const label = Math.max(0, Math.floor(values[0]));
            colors[label] = values.slice(1, 4);
        } else if (values.length >= 3) {
            colors.push(values.slice(0, 3));
        }
    });
    return colors.filter(Boolean).length ? colors : [[0.75, 0.75, 0.75]];
};


export const colorForAtlasValue = (value, colorMap, min, max) => {
    const direct = colorMap[Math.floor(value)];
    if (direct) return direct;
    if (colorMap.length === 1 || max <= min) return colorMap[0];
    const scaled = ((value - min) / (max - min)) * (colorMap.length - 1);
    const index = Math.max(0, Math.min(colorMap.length - 1, Math.round(scaled)));
    return colorMap[index] || colorMap[0];
};


export const getBrainBrowserShapes = (viewer) => {
    if (!viewer?.model?.children) return [];
    return viewer.model.children.filter(child => (
        child?.geometry?.attributes?.color?.array &&
        child?.userData?.original_data
    ));
};


export const applyAtlasTextIntensityToViewer = ({ viewer, values, colorMapText }) => {
    const modelData = viewer?.model_data?.get?.();
    const vertexCount = Math.floor((modelData?.vertices?.length || 0) / 3);
    if (!viewer || !modelData || !vertexCount) {
        throw new Error('Load the brain surface before applying atlas intensity colors.');
    }
    if (!values || values.length !== vertexCount) {
        throw new Error(`Atlas intensity length (${values?.length || 0}) does not match the loaded surface vertices (${vertexCount}).`);
    }

    const colorMap = parseBrainBrowserColorMap(colorMapText);
    let min = values[0];
    let max = values[0];
    for (let i = 1; i < values.length; i += 1) {
        min = Math.min(min, values[i]);
        max = Math.max(max, values[i]);
    }

    const colorArray = new Float32Array(values.length * 4);
    for (let i = 0; i < values.length; i += 1) {
        const color = colorForAtlasValue(values[i], colorMap, min, max);
        const offset = i * 4;
        colorArray[offset] = color[0];
        colorArray[offset + 1] = color[1];
        colorArray[offset + 2] = color[2];
        colorArray[offset + 3] = 1;
    }

    const shapes = getBrainBrowserShapes(viewer);
    shapes.forEach((shape) => {
        const geometryColor = shape.geometry.attributes.color.array;
        const indices = shape.userData.original_data.indices;
        const wireframe = shape.getObjectByName?.('__WIREFRAME__');
        const wireframeColor = wireframe?.geometry?.attributes?.color?.array;

        if (geometryColor.length === colorArray.length || !indices?.length) {
            for (let i = 0; i < geometryColor.length; i += 1) {
                geometryColor[i] = colorArray[i] ?? geometryColor[i];
            }
            if (wireframeColor && wireframeColor.length === colorArray.length) {
                wireframeColor.set(colorArray);
            }
        } else {
            for (let i = 0, count = indices.length; i < count; i += 3) {
                const ic = i * 4;
                const iwc = ic * 2;
                const indexA = indices[i] * 4;
                const indexB = indices[i + 1] * 4;
                const indexC = indices[i + 2] * 4;

                geometryColor[ic] = colorArray[indexA];
                geometryColor[ic + 1] = colorArray[indexA + 1];
                geometryColor[ic + 2] = colorArray[indexA + 2];
                geometryColor[ic + 3] = 1;
                geometryColor[ic + 4] = colorArray[indexB];
                geometryColor[ic + 5] = colorArray[indexB + 1];
                geometryColor[ic + 6] = colorArray[indexB + 2];
                geometryColor[ic + 7] = 1;
                geometryColor[ic + 8] = colorArray[indexC];
                geometryColor[ic + 9] = colorArray[indexC + 1];
                geometryColor[ic + 10] = colorArray[indexC + 2];
                geometryColor[ic + 11] = 1;

                if (wireframeColor) {
                    wireframeColor[iwc] = geometryColor[ic];
                    wireframeColor[iwc + 1] = geometryColor[ic + 1];
                    wireframeColor[iwc + 2] = geometryColor[ic + 2];
                    wireframeColor[iwc + 3] = 1;
                    wireframeColor[iwc + 4] = geometryColor[ic + 4];
                    wireframeColor[iwc + 5] = geometryColor[ic + 5];
                    wireframeColor[iwc + 6] = geometryColor[ic + 6];
                    wireframeColor[iwc + 7] = 1;
                    wireframeColor[iwc + 8] = geometryColor[ic + 4];
                    wireframeColor[iwc + 9] = geometryColor[ic + 5];
                    wireframeColor[iwc + 10] = geometryColor[ic + 6];
                    wireframeColor[iwc + 11] = 1;
                    wireframeColor[iwc + 12] = geometryColor[ic + 8];
                    wireframeColor[iwc + 13] = geometryColor[ic + 9];
                    wireframeColor[iwc + 14] = geometryColor[ic + 10];
                    wireframeColor[iwc + 15] = 1;
                    wireframeColor[iwc + 16] = geometryColor[ic + 8];
                    wireframeColor[iwc + 17] = geometryColor[ic + 9];
                    wireframeColor[iwc + 18] = geometryColor[ic + 10];
                    wireframeColor[iwc + 19] = 1;
                    wireframeColor[iwc + 20] = geometryColor[ic];
                    wireframeColor[iwc + 21] = geometryColor[ic + 1];
                    wireframeColor[iwc + 22] = geometryColor[ic + 2];
                    wireframeColor[iwc + 23] = 1;
                }
            }
        }

        shape.geometry.attributes.color.needsUpdate = true;
        if (wireframe?.geometry?.attributes?.color) {
            wireframe.geometry.attributes.color.needsUpdate = true;
        }
    });

    modelData.intensity_data = [{
        values,
        min,
        max,
        range_min: min,
        range_max: max,
        model_data: modelData
    }];
    viewer.render?.();
};
